import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ICONS } from 'src/constants/icons';
import { localeStore } from 'src/mobx/localesStore';
import { branchStore } from 'src/mobx/barnchStore';
import Icon from 'src/view/components/Controls/Icon/Icon';
import './style.scss';
import usePlacesAutocomplete, { getGeocode, getLatLng } from 'use-places-autocomplete';
import { DeliveryBranchSearchBoxResult } from '../DeliveryBranchSearchBoxResult/DeliveryBranchSearchBoxResult';

export interface IDeliveryBranchSearchBoxProps {
  setCenter?: any;
  address?: string | undefined;
  customClass?: string;
  searchMapIcon?: boolean;
  searchValue?: () => void;
  searchBranch?: boolean;
  searchAddress?: boolean;
  onSelectAutocompletedAddress?: (data: google.maps.places.PlaceResult) => void;
}

const DeliveryBranchSearchBox: FC<IDeliveryBranchSearchBoxProps> = (props: IDeliveryBranchSearchBoxProps) => {
  const [search, setSearch] = useState<string>(props.address ?? '');
  const [isInputOnFocus, setIsInputOnFocus] = useState(false);
  const [searchAddress] = useState(Boolean(props.searchAddress));
  const [searchValue, setSearchValue] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  const {
    ready,
    value,
    suggestions: { status, data, loading },
    clearSuggestions,
    setValue,
  } = usePlacesAutocomplete({
    debounce: 800,
  });

  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };

    updateWidth(); // Get initial width
    window.addEventListener('resize', updateWidth);

    return () => {
      window.removeEventListener('resize', updateWidth);
    };
  }, []);

  useEffect(() => {
    return () => {
      setValue('', false);
      clearSuggestions();
      if (props.searchBranch) {
        branchStore.setSearchValue(undefined);
      }
    };
  }, []);

  useEffect(() => {
    if (!props.address) {
      clear();
    }
  }, [props.address]);

  const onChangeText = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearch(event.target.value);
      setValue(event.target.value);

      if (props.searchBranch) {
        branchStore.setSearchValue(event.target.value);
      }
    },
    [props.searchBranch, setValue]
  );

  const clear = useCallback(() => {
    setSearch('');
  }, []);

  const handleBlur = useCallback((e: any) => {
    setSearchValue(e.target.value);
    setIsInputOnFocus(false);
  }, []);

  const handleFocus = useCallback((e: any) => {
    setIsInputOnFocus(true);
  }, []);

  const openMap = useCallback(() => {
    branchStore.togglePickupMap(true);
  }, []);

  const showPlaceholder = useCallback((): string => {
    return localeStore.t(searchAddress ? 'Address' : 'branch_name');
  }, [searchAddress]);

  const handleSelect = useCallback(
    async (address: any) => {
      /*
       * Clear all suggestions
       * */
      clearSuggestions();
      try {
        const results = await getGeocode({ address });
        const { lat, lng } = await getLatLng(results[0]);
        props.setCenter({ lat, lng }, null);
      } catch (error) {
        throw error;
      }
    },
    [clearSuggestions, props]
  );

  const searchResult = useMemo(() => {
    if (status === 'OK' && data.length && isInputOnFocus) {
      return (
        <div style={{ width: containerWidth }} onMouseDown={(e) => e.preventDefault()}>
          <div className="branches-searchbox-divider" />
          {data.map(({ place_id, structured_formatting, description }) => (
            <DeliveryBranchSearchBoxResult
              key={place_id}
              value={structured_formatting}
              description={description}
              selectAddress={handleSelect}
            />
          ))}
        </div>
      );
    }
    return null;
  }, [status, data, isInputOnFocus, handleSelect, containerWidth]);

  return (
    <div className="branches-searchbox-container" onFocus={handleFocus} onBlur={handleBlur}>
      <div
        ref={containerRef}
        className={`
        branches-searchbox-wrapper d-flex flex-grow-1
        ${searchValue.length && 'blur'}
        ${props.customClass ?? ' '}
        ${searchResult && 'with-result'}
      `}
      >
        <input
          value={search}
          onChange={onChangeText}
          className={`branches-searchbox-input ${searchResult && 'with-result'} color-text family-semibold`}
          placeholder={showPlaceholder()}
        />
        <Icon name={ICONS.SEARCH} className="branches-searchbox-icon color-gray" />

        {props.searchMapIcon ? (
          <Icon name={ICONS.MAP} onPress={openMap} className="branches-searchbox-open-map color-gray" />
        ) : null}
      </div>
      <div className="branches-searchbox-search-result">{searchResult}</div>
    </div>
  );
};

export default DeliveryBranchSearchBox;
